'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

export type Currency = string;

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  defaultCurrency: string;
  secondaryCurrency: string;
  eurRate: number;
  format: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [defaultCurrency, setDefaultCurrency] = useState<string>('ALL');
  const [secondaryCurrency, setSecondaryCurrency] = useState<string>('EUR');
  const [eurRate, setEurRate] = useState<number>(100.0);
  const [currency, setCurrencyState] = useState<Currency>('ALL');

  useEffect(() => {
    // 1. Fetch dynamic settings configured in Admin Panel
    let isMounted = true;
    fetch('/api/settings/public')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!isMounted || !data?.currency) return;
        if (data.currency.defaultCurrency) setDefaultCurrency(data.currency.defaultCurrency);
        if (data.currency.secondaryCurrency) setSecondaryCurrency(data.currency.secondaryCurrency);
        if (data.currency.eurRate) setEurRate(Number(data.currency.eurRate) || 100.0);

        const saved = localStorage.getItem('dyrma_currency');
        if (!saved) {
          setCurrencyState(data.currency.defaultCurrency || 'ALL');
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch public currency settings:', err);
      });

    // 2. Load stored user preference from localStorage
    const saved = localStorage.getItem('dyrma_currency');
    if (saved) {
      setCurrencyState(saved);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  const setCurrency = (c: Currency) => {
    setCurrencyState(c);
    localStorage.setItem('dyrma_currency', c);
  };

  const format = (amount: number): string => {
    if (currency === secondaryCurrency && (secondaryCurrency === 'EUR' || currency === 'EUR')) {
      const inEur = amount / eurRate;
      return `${inEur.toFixed(2)} €`;
    }
    if (currency === secondaryCurrency && secondaryCurrency !== 'EUR') {
      const converted = amount / eurRate;
      return `${converted.toFixed(2)} ${secondaryCurrency}`;
    }
    if (currency === 'ALL' || defaultCurrency === 'ALL') {
      return `${Math.round(amount).toLocaleString('sq-AL')} ALL`;
    }
    return `${Math.round(amount).toLocaleString('sq-AL')} ${currency || defaultCurrency}`;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        defaultCurrency,
        secondaryCurrency,
        eurRate,
        format,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
